import { API_TYPES, useGetQuery, type UseGetQueryOptions } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { minutesToMilliseconds } from "date-fns";
import { z } from "zod";

export const INCENTIVE_PROGRAM = {
  placementSignOnBonus: "placementSignOnBonus",
  placementCarePackage: "placementCarePackage",
} as const;
export const INCENTIVE_PROGRAMS = Object.values(INCENTIVE_PROGRAM);
export type IncentiveProgram = (typeof INCENTIVE_PROGRAMS)[number];

export const INCENTIVE_STATUS = {
  pending: "pending",
  paid: "paid",
} as const;
export const INCENTIVE_STATUSES = Object.values(INCENTIVE_STATUS);

const getIncentivesQueryForWorkerSchema = z.object({
  filter: z
    .object({
      program: z.nativeEnum(INCENTIVE_PROGRAM).optional(),
      sourceId: z.string().optional(),
      sourceType: z.string().optional(),
    })
    .optional(),
  page: z
    .object({
      size: z.number(),
      cursor: z.string().optional(),
    })
    .optional(),
});
export type GetIncentivesForWorkerQuery = z.infer<typeof getIncentivesQueryForWorkerSchema>;

const incentiveSchema = z.object({
  type: z.literal(API_TYPES.incentive),
  id: z.string(),
  attributes: z.object({
    program: z.nativeEnum(INCENTIVE_PROGRAM),
    amountInMinorUnits: z.number().optional(),
    currencyCode: z.string().optional(),
    status: z.nativeEnum(INCENTIVE_STATUS),
  }),
});
const incentivesResponseSchema = z.object({
  data: z.array(incentiveSchema),
  links: z.object({
    next: z.string().optional(),
    prev: z.string().optional(),
  }),
});
export type IncentivesResponse = z.infer<typeof incentivesResponseSchema>;

interface GetPlacementWorkplacesParams {
  workerId: string;
  queryParams: GetIncentivesForWorkerQuery;
}

export function useGetIncentives(
  params: GetPlacementWorkplacesParams,
  options?: UseGetQueryOptions<IncentivesResponse>
) {
  const { workerId, queryParams } = params;
  return useGetQuery({
    url: `${environmentConfig.REACT_APP_BASE_API_URL}/workers/${workerId}/incentives`,
    queryParams,
    requestSchema: getIncentivesQueryForWorkerSchema,
    responseSchema: incentivesResponseSchema,
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.GET_PLACEMENT_WORKPLACES_FAILURE,
      userErrorMessage: "Something went wrong while loading the placement workplaces.",
    },
    staleTime: minutesToMilliseconds(60),
    ...options,
  });
}
